import React from 'react';
import { designTokens } from '../../theme/designTokens';

export interface EmptyStateProps {
  /**
   * Icon component (from lucide-react)
   */
  icon: React.ComponentType<{ className?: string }>;

  /**
   * Main heading text
   */
  title: string;

  /**
   * Description/subtitle text
   */
  description?: string;

  /**
   * Action button/element
   */
  action?: React.ReactNode;

  /**
   * Show action only when certain conditions met
   * (e.g., no search/filter active)
   */
  showAction?: boolean;

  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * EmptyState Component
 *
 * Displays a consistent empty state with icon, message, and optional action.
 * Used when lists/tables have no data to display.
 *
 * @example
 * ```tsx
 * <EmptyState
 *   icon={FolderOpen}
 *   title="No projects found"
 *   description="Get started by creating your first project."
 *   action={<Button>New Project</Button>}
 * />
 *
 * <EmptyState
 *   icon={Users}
 *   title="No clients match your search"
 *   description="Try adjusting your filters or search term."
 * />
 * ```
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon,
  title,
  description,
  action,
  showAction = true,
  className = '',
}) => {
  return (
    <div className={`text-center py-12 ${className}`}>
      {/* Icon */}
      <Icon className="mx-auto h-12 w-12 text-gray-400" />

      {/* Title */}
      <h3 className="mt-2 text-sm font-medium text-gray-900">{title}</h3>

      {/* Description */}
      {description && (
        <p className="mt-1 text-sm text-gray-500">{description}</p>
      )}

      {/* Action Button */}
      {action && showAction && (
        <div className="mt-6">{action}</div>
      )}
    </div>
  );
};

export default EmptyState;
